import * as React from 'react';
import {FormComponentProps} from 'antd/lib/form';
import {Button, Form, Select} from 'antd';
import ExchangeRateRefreshButton from '../../common/ExchangeRateRefreshButton';
import locales from '../../locales';
import InputFormItem from '../../common/form-items/InputFormItem';
import {IncomingInvoice, Partner} from '../../api/dtos/dtos';
import * as dateUtils from '../../utils/date-utils';
import {MOMENT_DATE_FORMAT} from '../../utils/date-utils';
import DatePickerFormItem from '../../common/form-items/DatePickerFormItem';
import SubmitCancelFormItem from '../../common/form-items/SubmitCancelFormItem';
import StaticFormItem from '../../common/form-items/StaticFormItem';
import {groupNumber, numberGroupFormatter, numberGroupParser} from '../../utils/number-utils';
import NumberFormItem from '../../common/form-items/NumberFormItem';
import * as moment from 'moment';
import {getPaymentTypeOptions, PaymentType} from '../../common/PaymentType';
import SelectFormItem from '../../common/form-items/SelectFormItem';
import {renderIncomingInvoiceStateElement} from '../incoming-invoice-state';
import {isIncomingInvoiceOverdue, renderOverdueIcon} from '../incoming-invoice-overdue';
import {showSaveConfirm} from '../../utils/dialogs';
import PartnerAutoCompleteFormItem from './PartnerAutoCompleteFormItem';
import AutoCompleteFormItem from "../../common/form-items/AutoCompleteFormItem";
import {calculateHufSum, CURRENCY_HUF, getCurrencyOptions} from "../../utils/currency-utils";

const FIELD_NAME = 'name';
const FIELD_TYPE = 'type';
const FIELD_COMPANY_NAME = 'companyName';
const FIELD_BANK_NAME = 'bankName';
const FIELD_BANK_ACCOUNT_NUMBER = 'bankAccountNumber';
const FIELD_IBAN = 'iban';
const FIELD_INVOICE_NUMBER = 'invoiceNumber';
const FIELD_RESPONSIBLE_USER = 'responsibleUser';
const FIELD_PAYMENT_TYPE = 'paymentType';
const FIELD_ISSUE_DATE = 'issueDate';
const FIELD_COMPLETION_DATE = 'completionDate';
const FIELD_PAYMENT_DEADLINE = 'paymentDeadline';
const FIELD_PAID_DATE = 'paidDate';
const FIELD_SUM = 'sum';
const FIELD_CURRENCY = 'currency';
const FIELD_EXCHANGE_RATE = 'exchangeRate';
const FIELD_COMMENT = 'comment';

interface Props extends FormComponentProps {
  editable: boolean;
  types: string[];
  partners: Partner[];
  users: string[];
  invoice?: IncomingInvoice;
  defaultEurExchangeRate?: number;
  onRefreshDefaultExchangeRate: (completionDate?: moment.Moment) => Promise<void> | void;
  onPartnerSearch: (value: string) => void;
  onPartnerDelete: (id: number) => void;
  onFormSubmit: (invoice: IncomingInvoice) => void;
  onFormCancel: () => void;
}

interface State {
  hasValidCompletionDate?: boolean;
}

class IncomingInvoiceForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasValidCompletionDate: moment(this.props.invoice?.completionDate).isBefore(moment()),
    };
  };

  public componentDidUpdate(prevProps: Props) {
    const {invoice, defaultEurExchangeRate, form} = this.props;
    if (invoice || defaultEurExchangeRate == null || prevProps.defaultEurExchangeRate === defaultEurExchangeRate) {
      return;
    }
    const currency = form.getFieldValue(FIELD_CURRENCY) || CURRENCY_HUF;
    const exchangeRate = form.getFieldValue(FIELD_EXCHANGE_RATE);
    if (currency !== CURRENCY_HUF && exchangeRate == null) {
      form.setFieldsValue({[FIELD_EXCHANGE_RATE]: defaultEurExchangeRate});
    }
  }

  public render() {
    const {
      types,
      form,
      editable,
      invoice,
      partners,
      onPartnerSearch,
      onPartnerDelete,
      defaultEurExchangeRate
    } = this.props;
    const {hasValidCompletionDate} = this.state;

    const disabled = !editable;
    const initialDate = moment();
    const paymentMethodDisabled = invoice !== undefined;
    const paymentType = form.getFieldValue(FIELD_PAYMENT_TYPE);
    const bankAccountNumberRequired = paymentType === PaymentType.TRANSFER;
    const showPaidDate = paymentType === PaymentType.CASH || (invoice && invoice.paidDate);
    const initialPaidDate = invoice
      ? this.toMoment(invoice.paidDate)
      : paymentType === PaymentType.CASH ? initialDate : null;
    const currency = form.getFieldValue(FIELD_CURRENCY) || (invoice ? invoice.currency : CURRENCY_HUF);
    const isNonHuf = currency && currency !== CURRENCY_HUF;
    const currencyOption = getCurrencyOptions().find(opt => opt.value === currency);
    const sumPrecision = currencyOption?.fraction ? 2 : 0;
    const sum = form.getFieldValue(FIELD_SUM);
    const exchangeRate = form.getFieldValue(FIELD_EXCHANGE_RATE);
    const hufSum = calculateHufSum(sum, currency, exchangeRate);
    return (
      <Form>
        {invoice && invoice.state && (
          <StaticFormItem
            label={locales.state}
            initialValue={this.renderState(invoice)}
          />
        )}
        <InputFormItem
          form={form}
          fieldId={FIELD_INVOICE_NUMBER}
          label={locales.invoice_number}
          initialValue={invoice ? invoice.invoiceNumber : null}
          required={true}
          disabled={disabled}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_NAME}
          label={locales.appellation}
          initialValue={invoice ? invoice.name : null}
          required={true}
          disabled={disabled}
        />
        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_TYPE}
          label={locales.type}
          initialValue={invoice ? invoice.type : null}
          required={true}
          disabled={disabled}
          dataSource={types}
          validateOnDataSource={false}
        />
        <SelectFormItem
          form={form}
          fieldId={FIELD_PAYMENT_TYPE}
          label={locales.paymentType}
          initialValue={invoice ? invoice.paymentType : null}
          required={true}
          disabled={paymentMethodDisabled}
          options={getPaymentTypeOptions()}
        />
        <PartnerAutoCompleteFormItem
          form={form}
          fieldId={FIELD_COMPANY_NAME}
          label={locales.company_name}
          initialValue={invoice ? invoice.companyName : null}
          required={true}
          disabled={disabled}
          partners={partners}
          onSearch={onPartnerSearch}
          onDelete={onPartnerDelete}
          onSelect={this.fillBankDetails}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_BANK_ACCOUNT_NUMBER}
          label={locales.bankAccount}
          initialValue={invoice ? invoice.bankAccountNumber : null}
          required={bankAccountNumberRequired}
          disabled={disabled}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_BANK_NAME}
          label={locales.bank}
          initialValue={invoice ? invoice.bankName : null}
          required={false}
          disabled={disabled}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_IBAN}
          label={locales.iban}
          initialValue={invoice ? invoice.iban : null}
          required={false}
          disabled={disabled}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_INVOICE_NUMBER}
          label={locales.invoice_number}
          initialValue={invoice ? invoice.invoiceNumber : null}
          required={true}
          disabled={disabled}
        />

        <DatePickerFormItem
          form={form}
          fieldId={FIELD_ISSUE_DATE}
          label={locales.issue_date}
          initialValue={invoice ? this.toMoment(invoice.issueDate) : initialDate}
          required={true}
          showTime={false}
          format={MOMENT_DATE_FORMAT}
          disabled={disabled}
        />
        <DatePickerFormItem
          form={form}
          fieldId={FIELD_COMPLETION_DATE}
          label={locales.completion_date}
          initialValue={invoice ? this.toMoment(invoice.completionDate) : null}
          required={false}
          showTime={false}
          onChange={this.onCompletionDateChange}
          format={MOMENT_DATE_FORMAT}
          disabled={disabled}
        />
        <DatePickerFormItem
          form={form}
          fieldId={FIELD_PAYMENT_DEADLINE}
          label={locales.payment_deadline}
          initialValue={invoice ? this.toMoment(invoice.paymentDeadline) : initialDate}
          required={true}
          showTime={false}
          format={MOMENT_DATE_FORMAT}
          disabled={disabled}
        >
          <div>A kiállítás dátumához képest&nbsp;
            {this.renderDayButton(8, disabled)}&nbsp;
            {this.renderDayButton(15, disabled)}&nbsp;
            {this.renderDayButton(30, disabled)}&nbsp;
            {this.renderDayButton(45, disabled)}&nbsp;nap
          </div>
        </DatePickerFormItem>
        {showPaidDate &&
            <DatePickerFormItem
                form={form}
                fieldId={FIELD_PAID_DATE}
                label={locales.paid}
                initialValue={initialPaidDate}
                required={true}
                showTime={false}
                format={MOMENT_DATE_FORMAT}
                disabled={disabled}
            />
        }
        <NumberFormItem
          form={form}
          fieldId={FIELD_SUM}
          label={locales.sum}
          initialValue={invoice ? invoice.sum : null}
          required={true}
          disabled={disabled}
          min={-1_000_000_000}
          max={1_000_000_000}
          precision={sumPrecision}
          parser={numberGroupParser}
          formatter={numberGroupFormatter}
        >
          {disabled
            ? <span style={{marginLeft: 8}}>{isNonHuf ? locales.currency_eur : locales.currency_huf}</span>
            : <span style={{marginLeft: 8, display: 'inline-block'}}>
                {form.getFieldDecorator(FIELD_CURRENCY, {
                  initialValue: invoice ? invoice.currency : CURRENCY_HUF,
                  rules: [{required: true, message: locales.mandatory}],
                })(
                  <Select style={{width: 64}}>
                    {getCurrencyOptions().map(opt =>
                      <Select.Option key={opt.key} value={opt.value}>{opt.title}</Select.Option>
                    )}
                  </Select>
                )}
              </span>
          }
        </NumberFormItem>
        {isNonHuf &&
            <NumberFormItem
                form={form}
                fieldId={FIELD_EXCHANGE_RATE}
                label={locales.exchange_rate}
                initialValue={invoice?.exchangeRate ?? defaultEurExchangeRate}
                required={true}
                disabled={disabled}
                min={0}
                precision={2}
                parser={numberGroupParser}
                formatter={numberGroupFormatter}
            >
              {hasValidCompletionDate &&
                  <ExchangeRateRefreshButton
                    disabled={disabled}
                    onRefresh={this.refreshDefaultExchangeRate}
                  />
              }
            </NumberFormItem>
        }
        {isNonHuf &&
            <StaticFormItem
                label={locales.huf_sum}
                initialValue={hufSum ? <>{groupNumber(hufSum)} {locales.app_currency}</> : '-'}
            />
        }

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_RESPONSIBLE_USER}
          label={locales.responsible_user}
          initialValue={invoice ?  invoice.responsibleUser : null}
          required={false}
          disabled={disabled}
          allowClear={true}
          dataSource={this.props.users}
          validateOnDataSource={true}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_COMMENT}
          label={locales.comment}
          initialValue={invoice ? invoice.comment : null}
          required={false}
          disabled={disabled}
        />
        <SubmitCancelFormItem disabled={disabled} onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>
      </Form>
    );
  }

  private renderDayButton = (day: number, disabled: boolean) => {
    return <Button
      type="link"
      style={{padding: 0}}
      disabled={disabled}
      onClick={this.addDays(day)}>+{day}</Button>;
  };

  private renderState = (invoice: IncomingInvoice) => {
    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        {renderIncomingInvoiceStateElement(invoice.state)}
        &nbsp;
        &nbsp;
        {isIncomingInvoiceOverdue(invoice) && renderOverdueIcon()}
      </div>
    )
  };

  private onCompletionDateChange = (date?: moment.Moment) => {
    this.setState({hasValidCompletionDate: date && date.isValid() && date.isBefore(moment())})
  }

  private toMoment = (dateStr?: string) => {
    return dateStr ? dateUtils.toMomentOrThrow(dateStr) : null;
  };

  private addDays = (days: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const {form} = this.props;
    const created: moment.Moment = moment(form.getFieldValue(FIELD_ISSUE_DATE));
    const deadline = created.add(days, "days");
    form.setFieldsValue({
      [FIELD_PAYMENT_DEADLINE]: deadline,
    });
  };

  private fillBankDetails = (partner: Partner) => {
    const {form} = this.props;
    form.setFieldsValue({
      [FIELD_BANK_ACCOUNT_NUMBER]: partner.bankAccountNumber,
      [FIELD_BANK_NAME]: partner.bankName,
      [FIELD_IBAN]: partner.iban,
    });
  };

  private refreshDefaultExchangeRate = () => {
    const {form, onRefreshDefaultExchangeRate} = this.props;
    const completionDate = form.getFieldValue(FIELD_COMPLETION_DATE);
    return onRefreshDefaultExchangeRate(moment.isMoment(completionDate) ? completionDate : undefined);
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {invoice, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        showSaveConfirm(() => {
          onFormSubmit({
            ...invoice,
            name: values[FIELD_NAME],
            type: values[FIELD_TYPE],
            companyName: values[FIELD_COMPANY_NAME],
            bankName: values[FIELD_BANK_NAME],
            bankAccountNumber: values[FIELD_BANK_ACCOUNT_NUMBER],
            iban: values[FIELD_IBAN],
            invoiceNumber: values[FIELD_INVOICE_NUMBER],
            responsibleUser: values[FIELD_RESPONSIBLE_USER],
            paymentType: values[FIELD_PAYMENT_TYPE],
            sum: values[FIELD_SUM],
            currency: values[FIELD_CURRENCY] || CURRENCY_HUF,
            hufSum: calculateHufSum(values[FIELD_SUM], values[FIELD_CURRENCY], values[FIELD_EXCHANGE_RATE]) || values[FIELD_SUM],
            exchangeRate: values[FIELD_EXCHANGE_RATE],
            issueDate: values[FIELD_ISSUE_DATE],
            completionDate: values[FIELD_COMPLETION_DATE],
            paymentDeadline: values[FIELD_PAYMENT_DEADLINE],
            paidDate: values[FIELD_PAID_DATE],
            comment: values[FIELD_COMMENT],
          });
        });
      }
    });
  }
}

export default Form.create<Props>()(IncomingInvoiceForm);


// WEBPACK FOOTER //
// ./src/incoming-invoices/details/IncomingInvoiceForm.tsx